import { useEffect, useState } from "react";
import apiFetch from "../../../../../shared/services/apiClient";
import { addDays } from "../../utils/dateUtils";
import styles from "../../styles/addInvoice.module.css";

/** Genera el concepto automático basado en el nombre del rubro y la fecha de emisión */
function conceptoAutoFromRubro(nombre, fechaEmision) {
  const MESES = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  // Usar la fecha del formulario si se pasa; si no, la fecha actual
  let fecha;
  if (fechaEmision) {
    // fechaEmision es "YYYY-MM-DD" — parsear sin conversión de zona horaria
    const [year, month] = fechaEmision.split("-").map(Number);
    fecha = { month: month - 1, year }; // month es base-0
  } else {
    const now = new Date();
    fecha = { month: now.getMonth(), year: now.getFullYear() };
  }
  const mes = MESES[fecha.month];
  return `${nombre} — ${mes} ${fecha.year}`;
}

/** True si el nombre del rubro contiene "agua" */
function esAgua(nombre = "") {
  return /agua/i.test(nombre);
}

/** Mensaje de error inline */
function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        color: "#f87171",
        fontSize: "0.77rem",
        marginTop: 4,
        fontWeight: 500,
      }}
    >
      <i
        className="fa-solid fa-circle-exclamation"
        style={{ fontSize: "0.72rem" }}
      />
      {msg}
    </div>
  );
}

export default function InvoiceDetailsBody({
  invoiceData,
  updateField,
  errors = {},
  onIsAguaChange,
  isAgua,
}) {
  const [invoiceNumber, setInvoiceNumber] = useState("...");
  const [rubros, setRubros] = useState([]);
  const [ultimaMedida, setUltimaMedida] = useState(null); // número | null
  const [cargandoMedida, setCargandoMedida] = useState(false);

  // ── Rubro actualmente seleccionado (objeto completo) ────────────────
  const rubroActual =
    rubros.find((r) => r.id === Number(invoiceData.rubro)) ?? null;
  const isAguaFlag = rubroActual ? esAgua(rubroActual.nombre) : false;

  async function fetchProximoNumero() {
    try {
      const res = await apiFetch("/api/finance/factura/proximo-numero/");
      const data = await res.json();
      setInvoiceNumber(data.proximo ?? "—");
    } catch {
      setInvoiceNumber("—");
    }
  }
  useEffect(() => {
    // Solo ejecutar si hay filial seleccionada y el rubro actual es de agua
    if (!invoiceData.filial || !isAgua) {
      setUltimaMedida(null);
      return;
    }

    setCargandoMedida(true);
    apiFetch(
      `/api/finance/factura/?filial=${invoiceData.filial}&cuenta_ingreso=${invoiceData.rubro}&ordering=-fecha_emision&page_size=1`,
    )
      .then((r) => r.json())
      .then((data) => {
        // Buscar la última factura de agua con medida_actual definida
        const facturas = data.results ?? [];
        const ultima = facturas.find(
          (f) => f.medida_actual !== null && f.medida_actual !== undefined,
        );
        if (ultima) {
          const medida = Number(ultima.medida_actual);
          setUltimaMedida(medida);
          updateField("medida_anterior", medida); // auto-rellenar
        } else {
          setUltimaMedida(null);
        }
      })
      .catch(() => setUltimaMedida(null))
      .finally(() => setCargandoMedida(false));
  }, [invoiceData.filial, isAgua]);

  // ── Cargar rubros al montar ─────────────────────────────────────────
  useEffect(() => {
    fetchProximoNumero();

    apiFetch("/api/finance/catalogo/detalle/?tipo=INGRESO")
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.results ?? []);
        setRubros(list);

        if (list.length === 0) return;

        const primero = list[0];

        // Si el hook aún no tiene un rubro real (null o id que no existe en la lista)
        const yaExiste =
          invoiceData.rubro &&
          list.some((r) => r.id === Number(invoiceData.rubro));
        if (!yaExiste) {
          // Sincronizar con la primera cuenta de ingreso
          updateField("rubro", primero.id);
          updateField("cuenta_ingreso", primero.id);
          // Concepto automático
          if (!invoiceData.concepto) {
            updateField(
              "concepto",
              conceptoAutoFromRubro(primero.nombre, invoiceData.fecha_emision),
            );
          }
          // Notificar si es agua
          if (onIsAguaChange) onIsAguaChange(esAgua(primero.nombre));
        } else {
          // Rubro ya válido — solo notificar isAgua
          const actual = list.find((r) => r.id === Number(invoiceData.rubro));
          if (onIsAguaChange && actual) onIsAguaChange(esAgua(actual.nombre));
        }
      })
      .catch(() => {});
  }, []);

  // ── Re-sincronizar cuando invoiceData.rubro se limpia (resetFields) ─
  // El useEffect de carga solo corre al montar. Cuando se llama resetFields()
  // y rubro vuelve a null, este efecto vuelve a aplicar el primer rubro
  // Y también refresca el número de factura.
  useEffect(() => {
    if (invoiceData.rubro === null && rubros.length > 0) {
      const primero = rubros[0];
      updateField("rubro", primero.id);
      updateField("cuenta_ingreso", primero.id);
      updateField(
        "concepto",
        conceptoAutoFromRubro(primero.nombre, invoiceData.fecha_emision),
      );
      if (onIsAguaChange) onIsAguaChange(esAgua(primero.nombre));
      fetchProximoNumero();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData.rubro, rubros.length]);

  // ── Notificar al padre cuando isAgua cambia ─────────────────────────
  useEffect(() => {
    if (onIsAguaChange && rubroActual) {
      onIsAguaChange(isAguaFlag);
    }
  }, [isAguaFlag]);

  // ── Cambio de rubro ─────────────────────────────────────────────────
  function handleRubroChange(id) {
    const nuevo = rubros.find((r) => r.id === Number(id));
    if (!nuevo) return;

    // rubro y cuenta_ingreso usan el mismo ID (CuentaContable de ingreso)
    updateField("rubro", nuevo.id);
    updateField("cuenta_ingreso", nuevo.id);

    // Concepto automático — siempre se actualiza al cambiar rubro
    updateField(
      "concepto",
      conceptoAutoFromRubro(nuevo.nombre, invoiceData.fecha_emision),
    );

    // Si la cuenta no contiene "agua", limpiar medidas
    if (!esAgua(nuevo.nombre)) {
      updateField("medida_anterior", "");
      updateField("medida_actual", "");
    }
  }

  // ── Fecha de emisión ─────────────────────────────────────────────────
  function handleEmissionDate(date) {
    updateField("fecha_emision", date);
    updateField("fecha_vencimiento", date ? addDays(date, 5) : "");
    // Actualizar el concepto automático con el nuevo mes
    if (rubroActual) {
      updateField("concepto", conceptoAutoFromRubro(rubroActual.nombre, date));
    }
  }

  // ── Consumo agua preview ─────────────────────────────────────────────
  const consumo =
    isAguaFlag &&
    invoiceData.medida_anterior !== "" &&
    invoiceData.medida_actual !== ""
      ? (
          Number(invoiceData.medida_actual) -
          Number(invoiceData.medida_anterior)
        ).toFixed(2)
      : null;

  const consumoNegativo = consumo !== null && Number(consumo) < 0;

  return (
    <div className={styles["invoice-details-body"]}>
      {/* ── N° Factura + Rubro ──────────────────────────────────── */}
      <div className="row">
        <div className="col">
          <label>N° Factura</label>
          <input
            className="form-control"
            value={invoiceNumber}
            disabled
            title="Número asignado automáticamente al guardar"
          />
        </div>

        <div className="col">
          <label>
            Rubro <span style={{ color: "#f87171" }}>*</span>
          </label>
          <select
            className="form-select"
            value={invoiceData.rubro ?? ""}
            onChange={(e) => handleRubroChange(e.target.value)}
            style={errors.rubro ? { borderColor: "#ef4444" } : undefined}
          >
            {rubros.length === 0 && <option value="">Cargando rubros…</option>}
            {rubros.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nombre}
              </option>
            ))}
          </select>
          <FieldError msg={errors.rubro} />
        </div>
      </div>

      {/* ── Medidas de agua (aparece cuando rubro es agua) ─────── */}
      {isAguaFlag && (
        <div
          style={{
            background: "rgba(21,101,192,0.08)",
            border: "1px solid rgba(21,101,192,0.25)",
            borderLeft: "3px solid #1565C0",
            borderRadius: 6,
            padding: "12px 14px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              marginBottom: 10,
              color: "#90CAF9",
              fontWeight: 600,
              fontSize: "0.83rem",
            }}
          >
            <i className="fa-solid fa-droplet" />
            Lecturas del medidor de agua
          </div>

          <div className="row">
            <div className="col">
              <label>
                Medida anterior (m³)
                <span style={{ color: "#f87171", marginLeft: 3 }}>*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                placeholder={
                  cargandoMedida ? "Buscando última lectura…" : "Ej: 100.50"
                }
                value={invoiceData.medida_anterior}
                onChange={(e) => updateField("medida_anterior", e.target.value)}
                readOnly={ultimaMedida !== null}
                style={
                  ultimaMedida !== null
                    ? {
                        background: "rgba(21,101,192,0.08)",
                        borderColor: "#1565C0",
                        color: "#90CAF9",
                        cursor: "not-allowed",
                      }
                    : undefined
                }
              />

              <FieldError msg={errors.medida_anterior} />

              {/* Aviso cuando el dato viene del sistema */}
              {ultimaMedida !== null && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: "0.74rem",
                    color: "#90CAF9",
                  }}
                >
                  <i className="fa-solid fa-lock" style={{ marginRight: 5 }} />
                  Tomada de la última factura de agua — solo cambia la medida
                  actual
                </div>
              )}

              {cargandoMedida && (
                <i
                  className="fa-solid fa-circle-notch fa-spin"
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#60a5fa",
                    fontSize: "0.8rem",
                  }}
                />
              )}
            </div>

            <div className="col">
              <label>
                Medida actual (m³)
                <span style={{ color: "#f87171", marginLeft: 3 }}>*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                placeholder="Ej: 115.75"
                value={invoiceData.medida_actual}
                onChange={(e) => updateField("medida_actual", e.target.value)}
                style={
                  errors.medida_actual || consumoNegativo
                    ? {
                        borderColor: "#ef4444",
                        boxShadow: "0 0 0 2px rgba(239,68,68,0.2)",
                      }
                    : undefined
                }
              />
              <FieldError msg={errors.medida_actual} />
            </div>
          </div>

          {consumo !== null && (
            <div
              style={{
                marginTop: 8,
                fontSize: "0.82rem",
                fontWeight: 600,
                color: consumoNegativo ? "#f87171" : "#81C784",
              }}
            >
              {consumoNegativo
                ? "⚠️ La medida actual no puede ser menor que la anterior"
                : `💧 Consumo: ${consumo} m³`}
            </div>
          )}
          <div style={{ marginTop: 6, fontSize: "0.74rem", color: "#64748b" }}>
            Campos obligatorios para facturas de agua
          </div>
        </div>
      )}

      {/* ── Concepto (auto, editable) ────────────────────────────── */}
      <div>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          Concepto
          <span
            style={{
              fontSize: "0.68rem",
              color: "#9ca3af",
              background: "#1f2937",
              border: "1px solid #374151",
              borderRadius: 4,
              padding: "1px 6px",
              fontWeight: 400,
            }}
          >
            auto
          </span>
        </label>
        <input
          className="form-control"
          value={invoiceData.concepto}
          onChange={(e) => updateField("concepto", e.target.value)}
          placeholder="Se genera automáticamente del rubro"
        />
        <div style={{ fontSize: "0.73rem", color: "#6b7280", marginTop: 3 }}>
          Generado del rubro. Podés editarlo manualmente.
        </div>
      </div>

      {/* ── Fechas ──────────────────────────────────────────────── */}
      <div className="row">
        <div className="col">
          <label>
            Fecha emisión <span style={{ color: "#f87171" }}>*</span>
          </label>
          <input
            type="date"
            className="form-control"
            value={invoiceData.fecha_emision}
            onChange={(e) => handleEmissionDate(e.target.value)}
            style={
              errors.fecha_emision ? { borderColor: "#ef4444" } : undefined
            }
          />
          <FieldError msg={errors.fecha_emision} />
        </div>

        <div className="col">
          <label>
            Fecha vencimiento <span style={{ color: "#f87171" }}>*</span>
          </label>
          <input
            type="date"
            className="form-control"
            value={invoiceData.fecha_vencimiento}
            onChange={(e) => updateField("fecha_vencimiento", e.target.value)}
            style={
              errors.fecha_vencimiento ? { borderColor: "#ef4444" } : undefined
            }
          />
          <FieldError msg={errors.fecha_vencimiento} />
        </div>
      </div>

      {/* ── Monto ───────────────────────────────────────────────── */}
      <label>
        Monto <span style={{ color: "#f87171" }}>*</span>
      </label>
      <input
        type="number"
        className="form-control"
        value={invoiceData.monto || ""}
        placeholder="0.00"
        onChange={(e) => updateField("monto", Number(e.target.value))}
        style={errors.monto ? { borderColor: "#ef4444" } : undefined}
      />
      <FieldError msg={errors.monto} />
    </div>
  );
}
